import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getMetricsForProfile } from './reportPageMetrics';
import { usePagedGuides } from './usePagedGuides';
import { useHardPageFlow } from './useHardPageFlow';
import { BRAND } from '../../../../config/brand';

const DEFAULT_SHEET_FOOTER_LABELS = {
  page: 'Pag.',
  of: 'di',
};

const resolvePaginationMode = (paginationMode, legacyLayoutMode) => {
  if (paginationMode) {
    return paginationMode;
  }
  if (legacyLayoutMode === 'continuous') {
    return 'continuous';
  }
  if (legacyLayoutMode === 'guides' || legacyLayoutMode === 'paged_guides') {
    return 'guides';
  }
  if (legacyLayoutMode === 'strict') {
    return 'strict';
  }
  return 'strict';
};

export function ReportPagedCanvas({
  contentRef,
  readOnly = false,
  showPageIndicator = true,
  paginationMode = 'strict',
  paginationProfile = 'screen',
  enableHardPageFlow = true,
  allowHardPageFlowInPrint = false,
  showSheetHeaderFooter = true,
  headerLabel = BRAND.reportTitle,
  sheetChromeVariant = 'default',
  sheetHeaderLabels = null,
  sheetFooterLabels = null,
  layoutMode = null,
  children,
  className = '',
}) {
  const baseMode = useMemo(
    () => resolvePaginationMode(paginationMode, layoutMode),
    [paginationMode, layoutMode]
  );
  const [isCompactViewport, setIsCompactViewport] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= 720 : false
  ));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onResize = () => {
      setIsCompactViewport(window.innerWidth <= 720);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const shouldUseMobileContinuous = paginationProfile === 'screen'
    && isCompactViewport
    && baseMode !== 'continuous';
  const effectiveMode = shouldUseMobileContinuous
    ? 'continuous'
    : baseMode;
  const resolvedMetrics = useMemo(
    () => getMetricsForProfile(paginationProfile),
    [paginationProfile]
  );
  const hardPageFlowEnabled = enableHardPageFlow
    && (paginationProfile !== 'print' || allowHardPageFlowInPrint);

  useHardPageFlow({
    contentRef,
    mode: effectiveMode,
    metrics: resolvedMetrics,
    enabled: hardPageFlowEnabled,
  });

  const {
    pageCount,
    currentPage,
    usablePageHeight,
    canvasMinHeight,
    metrics,
  } = usePagedGuides({
    contentRef,
    layoutMode: effectiveMode,
    metrics: resolvedMetrics,
  });

  const canvasClassName = [
    'report-paged-canvas',
    effectiveMode === 'continuous'
      ? 'report-paged-canvas--continuous'
      : 'report-paged-canvas--paged',
    effectiveMode === 'strict' ? 'report-paged-canvas--strict' : '',
    effectiveMode === 'guides' ? 'report-paged-canvas--guides' : '',
    effectiveMode === 'paged_guides' ? 'report-paged-canvas--guides' : '',
    paginationProfile === 'print' ? 'report-paged-canvas--profile-print' : 'report-paged-canvas--profile-screen',
    sheetChromeVariant === 'pdf' ? 'report-paged-canvas--chrome-pdf' : 'report-paged-canvas--chrome-default',
    readOnly ? 'report-paged-canvas--readonly' : '',
    className,
  ].filter(Boolean).join(' ');

  const sheetIndexes = useMemo(
    () => Array.from({ length: Math.max(1, pageCount) }, (_, index) => index),
    [pageCount]
  );

  const canvasStyle = {
    '--report-page-width': `${metrics.pageWidthPx}px`,
    '--report-page-height': `${usablePageHeight}px`,
    '--report-sheet-gap': `${metrics.sheetGapPx}px`,
    '--report-page-content-top-inset': `${Math.max(0, Number(metrics.contentTopInsetPx) || 0)}px`,
    '--report-page-content-bottom-inset': `${Math.max(0, Number(metrics.contentBottomInsetPx) || 0)}px`,
    minHeight: `${Math.max(1, canvasMinHeight)}px`,
  };
  const resolvedHeaderLabels = useMemo(() => ({
    left: String(sheetHeaderLabels?.left || headerLabel || BRAND.reportTitle),
    right: String(sheetHeaderLabels?.right || ''),
  }), [sheetHeaderLabels, headerLabel]);
  const resolvedFooterLabels = useMemo(() => ({
    page: String(sheetFooterLabels?.page || DEFAULT_SHEET_FOOTER_LABELS.page || 'Pag.').trim() || 'Pag.',
    of: String(sheetFooterLabels?.of || DEFAULT_SHEET_FOOTER_LABELS.of || 'di').trim() || 'di',
  }), [sheetFooterLabels]);
  const shouldShowStickyIndicator = effectiveMode !== 'continuous'
    && showPageIndicator
    && pageCount > 1
    && sheetChromeVariant !== 'pdf';

  return (
    <div
      className={canvasClassName}
      style={canvasStyle}
      data-report-pagination-profile={paginationProfile}
      data-report-sheet-chrome={sheetChromeVariant}
    >
      {shouldShowStickyIndicator ? (
        <div className="report-paged-canvas__indicator" aria-live="polite">
          Pag. {currentPage} di {pageCount}
        </div>
      ) : null}

      {effectiveMode !== 'continuous' ? (
        <div className="report-paged-canvas__guides" aria-hidden="true">
          {sheetIndexes.map((sheetIndex) => (
            <div
              key={`sheet-${sheetIndex}`}
              className="report-paged-canvas__sheet"
              style={{
                top: `${sheetIndex * (usablePageHeight + metrics.sheetGapPx)}px`,
              }}
            >
              {showSheetHeaderFooter ? (
                <div className="report-paged-canvas__sheet-header">
                  {sheetChromeVariant === 'pdf' ? (
                    <>
                      <span className="report-paged-canvas__sheet-header-title">{resolvedHeaderLabels.left}</span>
                      <span className="report-paged-canvas__sheet-header-report">{resolvedHeaderLabels.right}</span>
                    </>
                  ) : (
                    <>
                      <span className="report-paged-canvas__sheet-header-title">{headerLabel}</span>
                      <span className="report-paged-canvas__sheet-header-page">Pag. {sheetIndex + 1}</span>
                    </>
                  )}
                </div>
              ) : null}
              {showSheetHeaderFooter ? (
                <div className="report-paged-canvas__sheet-footer">
                  <span className="report-paged-canvas__sheet-footer-page">
                    {sheetChromeVariant === 'pdf'
                      ? `${resolvedFooterLabels.page}\u00A0${sheetIndex + 1}\u00A0${resolvedFooterLabels.of}\u00A0${Math.max(1, pageCount)}`
                      : `Pag. ${sheetIndex + 1} di ${Math.max(1, pageCount)}`}
                  </span>
                </div>
              ) : (
                <span className="report-paged-canvas__sheet-label">Pag. {sheetIndex + 1}</span>
              )}
            </div>
          ))}
        </div>
      ) : null}

      <div className="report-paged-canvas__content">
        {children}
      </div>
    </div>
  );
}

ReportPagedCanvas.propTypes = {
  contentRef: PropTypes.shape({ current: PropTypes.any }),
  readOnly: PropTypes.bool,
  showPageIndicator: PropTypes.bool,
  paginationMode: PropTypes.oneOf(['strict', 'guides', 'continuous']),
  paginationProfile: PropTypes.oneOf(['screen', 'print']),
  enableHardPageFlow: PropTypes.bool,
  allowHardPageFlowInPrint: PropTypes.bool,
  showSheetHeaderFooter: PropTypes.bool,
  headerLabel: PropTypes.string,
  sheetChromeVariant: PropTypes.oneOf(['default', 'pdf']),
  sheetHeaderLabels: PropTypes.shape({
    left: PropTypes.string,
    right: PropTypes.string,
  }),
  sheetFooterLabels: PropTypes.shape({
    page: PropTypes.string,
    of: PropTypes.string,
  }),
  layoutMode: PropTypes.oneOf(['strict', 'guides', 'paged_guides', 'continuous']),
  children: PropTypes.node,
  className: PropTypes.string,
};

ReportPagedCanvas.defaultProps = {
  contentRef: null,
  readOnly: false,
  showPageIndicator: true,
  paginationMode: 'strict',
  paginationProfile: 'screen',
  enableHardPageFlow: true,
  allowHardPageFlowInPrint: false,
  showSheetHeaderFooter: true,
  headerLabel: BRAND.reportTitle,
  sheetChromeVariant: 'default',
  sheetHeaderLabels: null,
  sheetFooterLabels: null,
  layoutMode: null,
  children: null,
  className: '',
};

export default ReportPagedCanvas;
